// Maps the Courses page state (view + filter selections) to/from the legacy URL
// format, so bookmarks made on the previous site keep working — and new URLs match
// it byte-for-byte. Mirrors the approach in calendar-url-state.js.
//
// The reference site emits three distinct formats:
//   * Pristine (every group = "All"), either view: all pairs alphabetically sorted,
//     `all_<g>=true`, bare `filters[<g>]=`, `view_option` last.
//   * Modified + Alphabetical (view_option=default): `view_option` first, then the
//     filter pairs alphabetically sorted, `all_<g>=0|1`, array `filters[<g>][]=` with a
//     leading empty placeholder, spaces encoded as `%2B`.
//   * Modified + Upcoming (view_option=calendar): filter pairs alphabetically sorted
//     first then `view_option` last, `all_<g>=true|false`, array `filters[<g>][]=` with
//     no placeholders, spaces encoded as `+`.

// Internal filter group id -> URL parameter key.
const GROUP_TO_URL = {
  alphabet: 'alpha_keys',
  'course-types': 'course_types',
  departments: 'departments',
};
const GROUP_IDS = Object.keys(GROUP_TO_URL);

// Internal view key <-> URL view_option value.
const VIEW_TO_URL = { alphabetical: 'default', upcoming: 'calendar' };
const URL_TO_VIEW = { default: 'alphabetical', calendar: 'upcoming' };

function encKey(key) {
  return encodeURIComponent(key);
}

// The alphabetical (filter-change) format encodes spaces as %2B; the upcoming
// (view-toggle) format encodes them as a literal '+'. Both decode back to a space.
function encValuePlusEscaped(value) {
  return encodeURIComponent(String(value)).replace(/%20/g, '%2B');
}
function encValuePlusSpace(value) {
  return encodeURIComponent(String(value)).replace(/%20/g, '+');
}

function pairsToQuery(pairs, encValue) {
  return pairs.map(([k, v]) => `${encKey(k)}=${encValue(v)}`).join('&');
}

function isPristine(filters) {
  return GROUP_IDS.every(g => {
    const selected = filters?.[g];
    return !Array.isArray(selected) || selected.includes('all');
  });
}

// Pristine form: every pair (filters + view_option) sorted alphabetically.
function serializePristine(viewValue) {
  const pairs = [];
  for (const g of GROUP_IDS) pairs.push([`filters[all_${GROUP_TO_URL[g]}]`, 'true']);
  for (const g of GROUP_IDS) pairs.push([`filters[${GROUP_TO_URL[g]}]`, '']);
  pairs.push(['view_option', viewValue]);
  pairs.sort((a, b) => a[0].localeCompare(b[0]));
  return pairsToQuery(pairs, encValuePlusEscaped);
}

// Modified + Alphabetical: view_option first, then filter pairs sorted alphabetically.
// Each group emits a leading empty `[]` placeholder; the all flag is 0 (has selection)
// or 1 (still all). Stable sort keeps each placeholder before its values.
function serializeModifiedDefault(viewValue, filters) {
  const filterPairs = [];
  for (const g of GROUP_IDS) {
    const urlKey = GROUP_TO_URL[g];
    const selected = filters?.[g] || [];
    const isAll = selected.includes('all');
    filterPairs.push([`filters[${urlKey}][]`, '']);
    if (!isAll) for (const v of selected) filterPairs.push([`filters[${urlKey}][]`, v]);
    filterPairs.push([`filters[all_${urlKey}]`, isAll ? '1' : '0']);
  }
  filterPairs.sort((a, b) => a[0].localeCompare(b[0]));
  return pairsToQuery([['view_option', viewValue], ...filterPairs], encValuePlusEscaped);
}

// Modified + Upcoming: filter pairs sorted alphabetically first, then view_option last.
// The all flag is true/false. A selected group uses the array form `filters[<g>][]=value`
// (no empty placeholder); an all group uses a bare empty `filters[<g>]=` (like pristine).
function serializeModifiedCalendar(viewValue, filters) {
  const filterPairs = [];
  for (const g of GROUP_IDS) {
    const urlKey = GROUP_TO_URL[g];
    const selected = filters?.[g] || [];
    const isAll = selected.includes('all');
    if (isAll) filterPairs.push([`filters[${urlKey}]`, '']);
    else for (const v of selected) filterPairs.push([`filters[${urlKey}][]`, v]);
    filterPairs.push([`filters[all_${urlKey}]`, isAll ? 'true' : 'false']);
  }
  filterPairs.sort((a, b) => a[0].localeCompare(b[0]));
  return pairsToQuery([...filterPairs, ['view_option', viewValue]], encValuePlusSpace);
}

export function serializeCoursesState({ view, filters }) {
  const viewValue = VIEW_TO_URL[view] || 'default';
  if (isPristine(filters)) return serializePristine(viewValue);
  return viewValue === 'calendar'
    ? serializeModifiedCalendar(viewValue, filters)
    : serializeModifiedDefault(viewValue, filters);
}

// Returns true if any value of the all_<group> key signals "all selected", false if it
// signals a specific selection, or null when the key is absent.
function allFlagOn(params, key) {
  const values = params.getAll(key);
  if (values.length === 0) return null;
  return values.some(v => v === 'true' || v === '1');
}

export function parseCoursesState(queryString) {
  const params = new URLSearchParams(queryString || '');
  const state = { view: null, filters: {} };

  if (params.has('view_option')) {
    state.view = URL_TO_VIEW[params.get('view_option')] || null;
  }

  for (const g of GROUP_IDS) {
    const urlKey = GROUP_TO_URL[g];
    const isAll = allFlagOn(params, `filters[all_${urlKey}]`);
    // Read both the array (`[]`) and bare key forms; URLSearchParams turns form '+' into
    // a space, so any remaining '+' came from a %2B and also stands for a space.
    const values = [
      ...params.getAll(`filters[${urlKey}][]`),
      ...params.getAll(`filters[${urlKey}]`),
    ]
      .map(v => v.replace(/\+/g, ' '))
      .filter(v => v !== '');
    // "All" when the flag says so or when there are no concrete selections.
    state.filters[g] = isAll === true || values.length === 0 ? ['all'] : values;
  }

  return state;
}
